import React, { useCallback, useEffect, useMemo, useState } from "react";
import { commonApi } from "../../constants/apis/adminCommunication";
import markerIcon from "../../assets/icons/marker_map.png";
import { IoCloseCircleSharp } from "react-icons/io5";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  MarkerClusterer,
  InfoWindow,
  Polyline,
} from "@react-google-maps/api";
import "./DummyComponents.css";
import UserMarker from "./UserMarker";
const MapWithDirections = ({ location, zoneId, trackId }) => {
  const [map, setMap] = useState(null);
  const [trackPath, setTrackPath] = useState([]);
  const [aHouseData, setAHouseData] = useState();
  const [houseData, setHouseData] = useState([]);
  const [houseClick, setHouseClick] = useState(null);
  const [sourceMarker, setSourceMarker] = useState(null);
  const [dumpyardMarker, setDumpyardMarker] = useState(null);
  const [destinationMarker, setDestinationMarker] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyD3BOe9dr06OEcgS6y6568CZooHZIHKLhM",
  });

  const containerStyle = {
    width: "100%",
    height: "555px",
  };

  const defaultCenter = {
    lat: 21.164103,
    lng: 79.059302,
  };

  const mapCenter = useMemo(() => {
    return location?.length
      ? { lat: location[0].latitude, lng: location[0].longitude }
      : defaultCenter;
  }, [location]);

  useEffect(() => {
    if (houseClick && map) {
      const selectedLatLng = {
        lat: Number(houseClick.latitude),
        lng: Number(houseClick.longitude),
      };
      map.panTo(selectedLatLng);
    }
  }, [houseClick, map]);

  const fetchTrackDetails = async (trackId) => {
    try {
      const res = await commonApi.getTrackById({ id: trackId });
      if (res?.status === "SUCCESS" && res.track?.trackCords?.length > 0) {
        const pathCoordinates = (res.track.trackCords?.[0] || []).map(
          (point) => ({
            lat: Number(point.latitude),
            lng: Number(point.longitude),
          })
        );
        setTrackPath(pathCoordinates);
        if (pathCoordinates.length && map && window.google) {
          const bounds = new window.google.maps.LatLngBounds();
          pathCoordinates.forEach((coord) => bounds.extend(coord));
          map.fitBounds(bounds);
        }
        if (map && window.google) {
          const bounds = new window.google.maps.LatLngBounds();
          pathCoordinates.forEach((coord) => bounds.extend(coord));
          map.fitBounds(bounds); // auto center & zoom
        }
        if (res?.track?.source && res?.track?.destination) {
          setSourceMarker({
            lat: Number(res?.track?.source?.latitude),
            lng: Number(res?.track?.source?.longitude),
          });
          setDestinationMarker({
            lat: Number(res?.track?.destination?.latitude),
            lng: Number(res?.track?.destination?.longitude),
          });
        }
        if (res?.dumpYard) {
          setDumpyardMarker({
            lat: Number(res.dumpYard.latitude),
            lng: Number(res.dumpYard.longitude),
            name: res.dumpYard.name,
          });
        }
      } else {
        setTrackPath([]);
      }
    } catch (error) {
      setTrackPath([]);
    }
  };

  useEffect(() => {
    if (trackId) {
      fetchTrackDetails(trackId);
    }
  }, [trackId]);

  const getAllDynamoDBHouses = async () => {
    const formData = {
      trackId,
      zoneId,
    };
    const responseData = await commonApi.liveTrackingGetAllDynamoHouses(
      formData
    );
    setHouseData(responseData?.house);
    // dispatch(addHouses(responseData?.house));
  };

  useEffect(() => {
    let interval;

    const fetchInitial = async () => {
      await getAllDynamoDBHouses();
    };

    fetchInitial();

    interval = setInterval(() => {
      getAllDynamoDBHouses();
    }, 10000);

    return () => {
      clearInterval(interval);
    };
  }, [trackId, zoneId]);

  useEffect(() => {
    if (map && houseClick) {
      map.panTo({
        lat: Number(houseClick.latitude),
        lng: Number(houseClick.longitude),
      });
    }
  }, [houseClick, map]);

  const locationVehicle = useMemo(() => {
    return (
      location?.filter(
        (loc) => loc.zoneId === zoneId && loc.trackId === trackId
      ) || []
    );
  }, [location, trackId, zoneId]);

  const groupedHouses = useMemo(() => {
    const map = new Map();
    houseData?.forEach((loc) => {
      if (!loc.latitude || !loc.longitude) return;
      const positionKey = `${loc.latitude}_${loc.longitude}`;
      if (!map.has(positionKey)) {
        map.set(positionKey, []);
      }
      map.get(positionKey).push(loc);
    });

    return Array.from(map.values());
  }, [houseData]);

  const getHomeIconByColor = (color) => {
    if (!window?.google?.maps) return null;

    let file;
    switch (color) {
      case "green":
        file = require("./../../assets/icons/home_green.jpg");
        break;
      case "yellow":
        file = require("./../../assets/icons/home_yellow.png");
        break;
      case "red":
      default:
        file = require("./../../assets/icons/home_red.jpg");
        break;
    }

    return {
      url: file,
      scaledSize: new window.google.maps.Size(25, 25),
      anchor: new window.google.maps.Point(10, 20),
    };
  };

  const getMarkerIconForHouse = (housesAtSameLocation) => {
    if (!window.google?.maps) return null;

    const firstHouse = housesAtSameLocation[0];
    const isFlatscheme = firstHouse.houseType === "flatscheme";

    if (isFlatscheme) {
      const total = housesAtSameLocation.length;
      const collected = housesAtSameLocation.filter(
        (h) => h.isCollected
      ).length;

      if (collected === 0) return getHomeIconByColor("red");
      if (collected === total) return getHomeIconByColor("green");
      return getHomeIconByColor("yellow");
    }

    const isCollected = firstHouse?.isCollected ?? false;
    return getHomeIconByColor(isCollected ? "green" : "red");
  };

  const fetchHouseData = async (id) => {
    let formData = {
      houseId: id,
    };
    const houseData = await commonApi.getAHouseGarbageData(formData);
    setAHouseData(houseData);
  };

  const onLoad = useCallback(function callback(map) {
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  const blackMarkerIcon = {
    url: require("./../../assets/icons/truck.png"),
    fillColor: "#EB00FF",
    scale: 0.5,
  };

  const customOpenPlotIcon = useMemo(
    () => ({
      url: require("./../../assets/img/openPlot.png"),
      fillColor: "#EB00FF",
      scale: 0.5,
    }),
    []
  );

  const getOutlineHomeMarkerIcon = (color = "#22b7d1") => {
    if (!window?.google?.maps) return null;

    const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V9.5z"/>
    </svg>
  `.trim();

    return {
      url: require("./../../assets/icons/home.png"),
      scaledSize: new window.google.maps.Size(20, 20),
      anchor: new window.google.maps.Point(10, 20),
      fillColor: "red",
    };
  };

  const customMarkerIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps) return null;
    return getOutlineHomeMarkerIcon("#0b4c57");
  }, [isLoaded]);

  const residents = useMemo(() => {
    if (!houseClick) return [];
    const owners = houseData.filter(
      (h) =>
        h?._id === houseClick?._id &&
        h?.latitude === houseClick?.latitude &&
        h?.longitude === houseClick?.longitude
    );

    return owners;
  }, [houseClick, houseData]);

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={
        houseClick
          ? {
              lat: Number(houseClick.latitude),
              lng: Number(houseClick.longitude),
            }
          : mapCenter
      }
      zoom={10}
      onLoad={(mapInstance) => {
        setMap(mapInstance);
        setMapLoaded(true);
      }}
      onUnmount={onUnmount}
      onClick={() => {
        setHouseClick(null); // Close InfoWindow on outside click
      }}
    >
      {location &&
        location?.length > 0 &&
        location?.map((loc, index) => (
          <UserMarker key={index} location={loc} />
        ))}

      {/* <Marker
            position={{
              lat: location[0]?.latitude,
              lng: location[0]?.longitude,
            }}
            icon={blackMarkerIcon}
          /> */}
      {/* Render markers */}

      {trackPath.length >= 1 && (
        <Polyline
          path={trackPath}
          options={{
            strokeColor: "#00C853", // Green
            strokeOpacity: 1,
            strokeWeight: 5,
          }}
        />
      )}
      {sourceMarker && (
        <Marker
          key="source"
          position={sourceMarker}
          icon={{
            path: window.google.maps.SymbolPath.MARKER,
            scale: 8,
            fillColor: "green",
            fillOpacity: 1,
            strokeWeight: 1,
          }}
          label={{
            // text: "Source",
            color: "#383636",
            fontWeight: "bold",
          }}
        />
      )}

      {destinationMarker && (
        <Marker
          key="destination"
          position={destinationMarker}
          icon={{
            path: window.google.maps.SymbolPath.MARKER,
            scale: 8,
            fillColor: "green",
            fillOpacity: 1,
            strokeWeight: 1,
          }}
          label={{
            // text: "Destination",
            color: "#3e3c3c",
            fontWeight: "bold",
          }}
        />
      )}

      <MarkerClusterer
        options={{
          imagePath:
            "https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m",
          gridSize: 80,
          minimumClusterSize: 4,
          averageCenter: true,
          zoomOnClick: true,
        }}
      >
        {(clusterer) => (
          <>
            {groupedHouses?.map((housesAtSameLocation, index) => {
              const firstHouse = housesAtSameLocation[0];
              return (
                <Marker
                  key={`house-${index}`}
                  position={{
                    lat: Number(firstHouse.latitude),
                    lng: Number(firstHouse.longitude),
                  }}
                  icon={getMarkerIconForHouse(housesAtSameLocation)}
                  clusterer={clusterer}
                  onClick={() => setHouseClick(firstHouse)}
                />
              );
            })}

            {dumpyardMarker && (
              <Marker
                key="dumpyard"
                position={{ lat: dumpyardMarker.lat, lng: dumpyardMarker.lng }}
                icon={{
                  url: require("./../../assets/icons/DUMP_YARD.webp"),
                  scaledSize: new window.google.maps.Size(30, 30),
                }}
                label={{
                  text: "Dumpyard",
                  color: "#4a235a",
                  fontWeight: "bold",
                }}
                clusterer={clusterer}
              />
            )}
          </>
        )}
      </MarkerClusterer>

      {houseClick && (
        <InfoWindow
          position={{
            lat: Number(houseClick.latitude) + 0.00004,
            lng: Number(houseClick.longitude),
          }}
          onCloseClick={() => {
            setHouseClick(null);
          }}
        >
          <div
            style={{
              minWidth: "260px",
              padding: "12px 16px",
              lineHeight: "1.5",
            }}
          >
            {houseClick?.houseType === "flatscheme" ? (
              <>
                <h4 style={{ marginBottom: "8px" }}>Flatscheme Residents</h4>
                {residents.map((r, i) => {
                  const iconSrc = r?.isCollected
                    ? require("./../../assets/icons/home_green.jpg")
                    : require("./../../assets/icons/home_red.jpg");

                  return (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: 6,
                      }}
                    >
                      <img
                        src={iconSrc}
                        alt={r?.isCollected ? "green house" : "red house"}
                        width={16}
                        height={16}
                        className="blinking-icon"
                        style={{ marginRight: 8, borderRadius: "50%" }}
                      />
                      <strong>{r?.ownerName}</strong> - {r?.plotNo}
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                <h4 style={{ marginBottom: "8px" }}>{houseClick?.ownerName}</h4>
                <p style={{ marginBottom: "6px" }}>
                  Plot No: {houseClick?.plotNo}
                </p>
              </>
            )}
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  ) : null;
};

export default MapWithDirections;
